<script>
import Button from '@/components/Button'
import IconButton from '@/components/IconButton'
import VueEditor from '@/components/vue2-editor/VueEditor'
import FloatingLabelTextInput from '@/components/FloatingLabelTextInput'
import Checkbox from '@/components/Checkbox'
import Theme from '@/theme'
import VueUtils from '@/utils/vueUtils'
import FileUtils from '@/utils/fileUtils'
import DOMUtils from '@/utils/DOMUtils'
import FileReaderUtils from '@/utils/fileReaderUtils'
import CameraUtils from '@/utils/cameraUtils'
import DateUtils from '@/utils/dateUtils'
import StringUtils from '@/utils/stringUtils'
import GeolocationUtils from '@/utils/geolocationUtils'
import ReportServices from '@/services/reports'
import EnvironmentUtils from '@/utils/environmentUtils'
import Config from '~/config/index'

const steps =
{
    reportTypeChoice: 0,
    informationInput: 1,
    reportSent: 2
}

export default
{
    name: 'report-view',

    render () 
    {
        return (
            <div id='report-view'>
                <div class='report-form'>
                    <inline-svg class='card-icon' src={require('@/assets/img/question.svg')} fill={Theme.properties.accentColor} />
                    <p class='title'>{ this.title }</p>
                    
                    <div class='choices-card' v-show={ this.step == steps.reportTypeChoice }>
                        <div class='choices' ref='choices'>
                            <Button text='Insalubrité publique' type='light' />
                            <Button text='Dégradation route, éclairage, matériel communal' type='light' />
                            <Button text='Dégradation personnelle' type='light' />
                            <Button text='Service communal' type='light' />
                            <Button text='Sécurité' type='light' />
                            <Button text='Autre' type='light' />
                        </div>
                    </div>

                    <div class='information-input' v-show={ this.step == steps.informationInput }>
                        <div class='picture-action' v-show={ this.reportPicture == undefined }>
                            <IconButton text='Prendre une photo' icon={require('@/assets/img/camera-outlined.svg')} on-click={this.onTakePictureClicked} />
                            <IconButton text='Sélectionner une photo' icon={require('@/assets/img/gallery.svg')} on-click={this.onSelectPictureClicked} />
                        </div>
                        <div class='report-picture-preview' v-show={ this.reportPicture != undefined } on-click={this.onReporPicturePreviewClicked}>
                            <img src={this.reportPicture} />
                        </div>
                        <VueEditor class='description' placeholder='Donner une description de votre signalement ici.' v-model={this.reportDescription}
                            show-toolbar={false} no-html={true} ref='description' />
                        <Checkbox icon={require('@/assets/img/pin.svg')} label='Ne pas communiquer ma géolocalisation' v-model={this.doNotSendGeolocation} />
                        <Checkbox icon={require('@/assets/img/question-mark.svg')} label='Je préfère rester anonyme' v-model={this.isAnonymousReport} />
                        <div v-show={ !this.isAnonymousReport }>
                            <FloatingLabelTextInput placeholder='Votre prénom' v-model={this.senderFirstName} ref='firstName' />
                            <FloatingLabelTextInput placeholder='Votre nom' v-model={this.senderLastName} ref='lastName' />
                            <FloatingLabelTextInput placeholder='Votre courriel' type='email' v-model={this.senderMail} ref='mail' />
                        </div>
                        <Button text='Soumettre ce signalement' on-click={this.onSendReportClicked} />
                        <p class='agreement-text'>En envoyant ce formulaire vous acceptez les <a href={Config.eulaUrl} target='_system'>CGU</a>.</p>
                    </div>

                    <div class='report-sent' v-show={ this.step == steps.reportSent }>
                        <inline-svg src={require('@/assets/img/smile.svg')} class='icon' />
                        <p class='thank-text'>{ this.senderFirstName ? `Merci ${this.senderFirstName}` : 'Merci' }, nous nous occupons de la suite.</p>
                        <Button class='button-go-home' text="Retourner à l'accueil" on-click={() => this.$router.push('/home')} />  
                    </div>
                </div>
            </div>
        )
    },

    created ()
    {
        this.step = 0
        this.reportPicture = undefined
    },

    data ()
    {
        return {
            title: '',
            step: undefined,
            reportType: '',
            isAnonymousReport: false,
            doNotSendGeolocation: false,
            reportPicture: undefined,
            reportDescription: '',
            senderLastName: '',
            senderFirstName: '',
            senderMail: '',
            geolocation: '',
        }
    },

    watch:
    {
        step: function ()
        {
            switch (this.step)
            {
                case steps.reportTypeChoice:
                    this.title = 'Quelle est la nature de votre signalement ?'
                    break;
                
                case steps.informationInput:
                    this.title = 'Quels sont les détails de votre signalement ?'
                    this.requestGeolocation()
                    break;

                case steps.reportSent:
                    this.title = DOMUtils.nbsp
                    break;
            }
        },

        doNotSendGeolocation: function ()
        {
            if (!this.doNotSendGeolocation) this.requestGeolocation()
        }
    },

    mounted ()
    {
        for (const children of this.$children)
        {
            const isButton = VueUtils.isInstanceOfComponent(children, Button)
            const isChoice = children.$el.parentElement.classList.contains('choices')

            if (isButton && isChoice)
            {
                children.$el.addEventListener('click', () => this.onReportTypeChoiceButtonClicked(children.text))
            }
        }
    },

    methods:
    {
        onReportTypeChoiceButtonClicked (reportType)
        {
            this.reportTypeChoiceStepCompleted(reportType)
        },
        
        reportTypeChoiceStepCompleted (reportType)
        {
            this.reportType = reportType
            this.step = steps.informationInput
        },

        async requestGeolocation ()
        {
            try
            {
                await GeolocationUtils.requestHightAccuracyGeolocationAsync()
                const location = await GeolocationUtils.getCurrentPositionAsync()

                this.geolocation = GeolocationUtils.coordsToString(location.coords)
            }
            catch (error)
            {
                this.$toast.warning('Nous ne pouvons pas accéder à votre géolocalisation, si vous pensez que c\'est une' +
                    ' information nécessaire vous pouvez l\'ajouter en description', { duration: 10000 })

                this.doNotSendGeolocation = true

                console.log(error)
            }
        },

        async onTakePictureClicked ()
        {
            try
            {
                this.reportPicture = await CameraUtils.capturePictureAsync()
            }
            catch (e)
            {
                if (typeof e == 'string' && e.toLowerCase().includes('no image selected'))
                {
                    return console.log('Launched picture capture but user did not take any image:', e)
                }
                console.error('Cannot access device camera', e)

                this.$toast.error('Votre appareil ne peut pas prendre de photos')
            }
        },

        async onSelectPictureClicked ()
        {
            const files = await FileUtils.promptImages()
            const file = files.item(0)

            this.reportPicture = await FileReaderUtils.readAsDataURLAsync(file)
        },

        onReporPicturePreviewClicked ()
        {
            this.reportPicture = undefined
        },

        async onSendReportClicked (event, button)
        {
            const areFieldsValid = this.checkFieldsValidity()
            
            if (!areFieldsValid) return
            
            const report =
            {
                reason: this.reportType,
                isAnonymous: this.isAnonymousReport,
                pictureUrl: this.reportPicture,
                description: this.reportDescription,
                senderLastName: this.senderLastName,
                senderFirstName: this.senderFirstName,
                senderMail: this.senderMail,
                geolocation: this.geolocation,
                date: DateUtils.stringDateNow()
            }

            if (this.doNotSendGeolocation) report.geolocation = undefined

            button.showSpinner = true

            await ReportServices.createReport(report, EnvironmentUtils.cityId)

            button.showSpinner = false

            this.step = steps.reportSent
        },

        checkFieldsValidity ()
        {
            let isValid = true

            if (StringUtils.isEmptyString(this.reportDescription))
            {
                this.$refs.description.setErrorMessage('Vous devez fournir une description de votre signalement')
                isValid = false
            }

            if (!this.isAnonymousReport)
            {
                if (StringUtils.isEmptyString(this.senderLastName))
                {
                    this.$refs.lastName.setErrorMessage('Votre nom est nécessaire')
                    isValid = false
                }
                if (StringUtils.isEmptyString(this.senderFirstName))
                {
                    this.$refs.firstName.setErrorMessage('Votre prénom est nécessaire')
                    isValid = false
                }
                if (StringUtils.isEmptyString(this.senderMail))
                {
                    this.$refs.mail.setErrorMessage('Nous avons besoin de votre courriel pour assurer un suivi personnalisé')
                    isValid = false
                }
            }

            return isValid
        }
    },

    components:
    {
        Button,
        IconButton,
        VueEditor,
        FloatingLabelTextInput,
        Checkbox
    }
}
</script>

<style lang='less'>
@import '~@/styles/main.less';

#report-view
{
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;

    .OnDesktop({
        border-left: var(--theme-vertical-menu-total-vertical-space) solid transparent;
    });

    .report-form
    {
        background: var(--theme-content-background-color);
        margin-top: calc(var(--theme-horizontal-menu-height) + 2vh);
        margin-bottom: calc(var(--theme-horizontal-menu-height) + 2vh);
        padding-bottom: 15px;
        border-radius: 10px;
        width: 90%;

        .OnTablet({
            padding-bottom: 30px;
        });

        .OnDesktop({
            margin-top: 5vh;
            margin-bottom: 5vh;
            width: 50%;
        });

        .card-icon
        {
            float: left;
            width: 25%;
            height: auto;
            margin-left: -4%;
            margin-top: -8%;
            margin-right: 15px;

            .OnTablet({
                margin-right: 35px;
                width: 15%;
            });

            .OnDesktop({
                width: 10%;
                margin-top: -3%;
                margin-right: 0;
            });

            .OnLarge({ margin-right: 20px; });
        }

        .title
        {
            font-size: 5vw;
            font-weight: 500;
            padding: 18px;

            .OnLargeMobile({ font-size: 2.5vh });

            .OnTablet({
                font-size: 2.5vh;
                padding-top: 5%;
            });
        }

        .choices-card
        {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            
            .OnTablet({
                margin-top: 30px;
                margin-bottom: 30px;
            });

            .choices
            {
                width: 90%;

                .OnTablet({
                    width: 80%;
                });

                .button
                {
                    margin-top: 2vh;
                    padding: 3.3vw;
                    width: 100%;

                    .OnTablet({
                        padding: 2.3vw;
                    });

                    .OnDesktop({
                        padding: 0.5vw 5vw;
                    });

                    .button-text
                    {
                        .OnDesktop({
                            padding: 10px;
                        });
                    }
                }
            }
        }

        .information-input
        {
            display: flex;
            flex-direction: column;
            margin: 0 9%;
            margin-top: 1vh;

            .OnTablet({
                margin-top: 4vh;
            });

            .OnDesktop({
                margin-left: 15%;
                margin-right: 15%;
            });

            .picture-action
            {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                width: 100%;
                
                .icon-button
                {
                    width: 35%;
                    box-shadow: var(--theme-box-shadow-soft);

                    .text
                    {
                        font-size: 80%;
                    }
                }
            }

            .report-picture-preview
            {
                height: 12vh;
                width: 100%;
                border-radius: 4px;
                cursor: pointer;

                img
                {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    border-radius: inherit;
                }
            }

            .description
            {
                margin-top: 5%;

                .ql-editor
                {
                    min-height: 20vw;

                    .OnTablet({ min-height: 10vw; });
                    .OnDesktop({ min-height: 4vw; })
                }
            }

            .checkbox
            {
                margin-top: 5%;

                .OnDesktop({
                    margin-top: 3%;
                });
            }

            .floating-label-text-input
            {
                margin-top: 5%;

                .OnDesktop({
                    margin-top: 15px;
                });
            }

            .button
            {
                margin-top: 10%;
                width: 100%;
            }

            .agreement-text
            {
                text-align: center;
                margin-top: 10px;
                color: var(--theme-text-color-extra-light);
                font-size: 70%;

                .OnTablet({
                    font-size: 1.2vh;
                });
            }
        }

        .report-sent
        {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;

            .OnDesktop({
                width: 80%;
                margin-left: auto;
                margin-right: auto;
                margin-bottom: 5%;
            });

            .icon
            {
                width: 35%;
                height: auto;
                fill: var(--theme-accent-color);

                .OnTablet({
                    width: 20%;
                    margin-top: 5%;
                });

                .OnDesktop({
                    width: 25%;
                    margin-top: 0;
                });
            }

            .thank-text
            {
                font-size: 5vw;
                font-family: Nunito;
                color: var(--theme-text-color-light);
                text-align: center;
                font-weight: bold;
                margin-top: 4vh;
                margin-left: 10%;
                margin-right: 10%;

                .OnTablet({ margin-top: 7vh; });

                .OnDesktop({
                    font-size: 27px;
                    margin-left: 0;
                    margin-right: 0;
                })
            }

            .button-go-home
            {
                width: 80%;
                margin-top: 8vh;
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/views/Report.vue